import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Loading } from 'secondstep-components'
import { ASSIGNING_LICENSE_TEXT } from 'lib/constants'
import { operations as siteOperations } from 'store/sitesManager'
import { operations as licenseOperations } from 'store/licenseManager'
import { paths } from 'components/LUMRouter/paths'
import getReassignSiteData, {
  isReassignSiteMissingFromSitesMe,
} from './getReassignSiteData'
import loadReassignSiteDataFallback from './loadReassignSiteDataFallback'
import LicenseManagement from './component'

const transformNetSuiteAssetsToUserLicenses = assets => {
  if (!Array.isArray(assets)) {
    return {
      activeLicenses: [],
      unassignedLicenses: [],
      inactiveLicenses: [],
      adminLicenses: [],
    }
  }

  const now = new Date()
  const activeNetSuiteAssets = []
  const unassignedNetSuiteAssets = []
  const inactiveNetSuiteAssets = []

  assets.forEach(asset => {
    const expirationDate = asset.expirationDate
      ? new Date(asset.expirationDate)
      : null
    const isInactive =
      (expirationDate && expirationDate < now) || asset.isActive === false
    const isAssigned = asset.site && asset.site.id

    if (isInactive) {
      inactiveNetSuiteAssets.push(asset)
    } else if (!isAssigned) {
      unassignedNetSuiteAssets.push(asset)
    } else {
      activeNetSuiteAssets.push(asset)
    }
  })

  return {
    activeLicenses: activeNetSuiteAssets,
    unassignedLicenses: unassignedNetSuiteAssets,
    inactiveLicenses: inactiveNetSuiteAssets,
    adminLicenses: activeNetSuiteAssets,
  }
}

export const getCancelAssignDestinationPath = (pathname, selectedLicense) => {
  if (
    pathname === paths.ASSIGN_LICENSE ||
    pathname === paths.BASE_LICENSE_MANAGEMENT
  ) {
    return Boolean(selectedLicense?.site?.id)
      ? paths.ASSIGNED_LICENSES
      : paths.UNASSIGNED_LICENSES
  }

  return pathname
}

const LicenseManagementContainer = ({ pushTo }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [selectedLicense, setSelectedLicense] = useState(null)
  const [fallbackSiteData, setFallbackSiteData] = useState(null)
  const [isLoadingReassignSiteData, setIsLoadingReassignSiteData] =
    useState(false)

  const { areSitesInitialized, currentUser, isUpdating, sites, userLicenses } =
    useSelector(state => {
      const { isLoading, netSuiteAssets } = state.netSuiteAssetManager
      const { profile } = state.userProfileManager

      const userLicenses = transformNetSuiteAssetsToUserLicenses(netSuiteAssets)

      return {
        areSitesInitialized: state.sitesManager?.sites?.isInitialized ?? false,
        currentUser: profile || {},
        isLoading,
        isUpdating: false,
        netSuiteAssets,
        sites: state.sitesManager?.sites?.data || [],
        userLicenses,
      }
    })

  const isReassign = Boolean(selectedLicense?.site?.id)
  const needsReassignFallback =
    isReassign &&
    areSitesInitialized &&
    isReassignSiteMissingFromSitesMe(selectedLicense, sites)

  const syncReassignSiteData = useMemo(
    () =>
      isReassign ? getReassignSiteData(selectedLicense, sites) : null,
    [isReassign, selectedLicense, sites],
  )

  const profileSiteData = useMemo(
    () => ({
      country: currentUser?.country ?? '',
      region: currentUser?.region ?? '',
      city: currentUser?.city ?? '',
      netSuiteCustomerId: currentUser?.netSuiteCustomerId ?? '',
      nsCustomerId: '',
      siteName: currentUser?.siteName ?? '',
    }),
    [currentUser],
  )

  const assignSiteData = isReassign
    ? needsReassignFallback
      ? fallbackSiteData ?? syncReassignSiteData
      : syncReassignSiteData
    : profileSiteData

  useEffect(() => {
    if (!needsReassignFallback || !selectedLicense?.id) {
      setFallbackSiteData(null)
      setIsLoadingReassignSiteData(false)
      return undefined
    }

    let isCancelled = false
    setFallbackSiteData(null)
    setIsLoadingReassignSiteData(true)

    loadReassignSiteDataFallback(selectedLicense.id)
      .then(siteData => {
        if (!isCancelled) {
          setFallbackSiteData(siteData)
        }
      })
      .catch(error => {
        console.error('Failed to load reassignment site data:', error)
        if (!isCancelled) {
          setFallbackSiteData(syncReassignSiteData)
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingReassignSiteData(false)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [needsReassignFallback, selectedLicense?.id, syncReassignSiteData])

  // Clear selectedLicense when navigating away from assign-license page
  useEffect(() => {
    if (location.pathname !== paths.ASSIGN_LICENSE && selectedLicense) {
      setSelectedLicense(null)
    }
  }, [location.pathname, selectedLicense])

  const onSelectLicense = selectedLicense => setSelectedLicense(selectedLicense)

  const clearSelectedLicense = () => {
    const returnPath = getCancelAssignDestinationPath(
      location.pathname,
      selectedLicense,
    )

    setSelectedLicense(null)
    dispatch(licenseOperations.fetchLicenses())
    dispatch(siteOperations.fetchSites())
    pushTo(returnPath)
  }

  const onSelectUserManagement = siteId => pushTo(`/user-management/${siteId}`)

  const onSelectAssign = () => pushTo('/license-management/assign-license')

  return (
    <>
      {isUpdating && (
        <Loading
          dataTestId="saving-assigning-license"
          loadingText={ASSIGNING_LICENSE_TEXT}
        />
      )}
      {!isUpdating && (
        <LicenseManagement
          assignSiteData={assignSiteData}
          clearSelectedLicense={clearSelectedLicense}
          currentUser={currentUser}
          isLoadingReassignSiteData={
            needsReassignFallback && isLoadingReassignSiteData
          }
          onSelectAssign={onSelectAssign}
          onSelectLicense={onSelectLicense}
          onSelectUserManagement={onSelectUserManagement}
          pushTo={pushTo}
          selectedLicense={selectedLicense}
          userLicenses={userLicenses}
        />
      )}
    </>
  )
}

LicenseManagementContainer.propTypes = {
  pushTo: PropTypes.func.isRequired,
}

export default LicenseManagementContainer
